/**
 * @file `useSignoutMutation` — POST /api/account/signout, then flip
 *       `qkMe` to `{ learner: null }` synchronously.
 * @module @labcraft/lab/ui/hooks/use-signout
 *
 * The synchronous `setQueryData(qkMe, { learner: null })` in
 * `onSuccess` is load-bearing — it's the fix for the post-signout
 * chrome-bounce regression. `invalidateQueries` would mark stale
 * and refetch on the next mount: between the signout response and
 * the refetch resolving, one render goes through with the signed-in
 * chrome still mounted under a now-signed-out cache. The imperative
 * cache write skips that race entirely. Same shape as admin's
 * Phase 9 use_signout.
 *
 * Convention: hooks live in this directory and never call
 * `useMutation` outside it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import { qkMe } from './query_keys.ts'

/**
 * Sign the current learner out. The mutation's `onSuccess` writes
 * `{ learner: null }` into the `qkMe` cache so the next render flips
 * the chrome to the anonymous branch before any signed-in component
 * can re-render with the stale identity.
 */
export function useSignoutMutation() {
  const client = useClient<Lab>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => client.account.signout(),
    onSuccess: () => {
      queryClient.setQueryData(qkMe, { learner: null })
    },
  })
}
